import React, { useState } from 'react';
import { useGetTestItemCommentsQuery, useAddTestItemCommentMutation } from '@app.raytd.com/store';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import CommentItem from './comment-item';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface CommentsSectionProps {
  testItemId: number;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ testItemId }) => {
  const [content, setContent] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  const {
    data: comments = [],
    isLoading,
    error,
  } = useGetTestItemCommentsQuery(testItemId);

  const [addComment, { isLoading: isAdding }] = useAddTestItemCommentMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      return;
    }

    try {
      await addComment({
        testItemId,
        content: content.trim(),
      }).unwrap();
      setContent('');
      toast.success('Comment added');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to add comment');
    }
  };

  const filteredComments = showResolved
    ? comments
    : comments.filter((comment) => comment.resolved_at === null);

  const unresolvedCount = comments.filter((c) => c.resolved_at === null).length;
  const resolvedCount = comments.length - unresolvedCount;

  if (error) {
    return (
      <div className="px-3 py-2 text-sm text-red-600">
        Failed to load comments
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-zinc-200 bg-white/80 px-3 py-2">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-950">
            Comments ({unresolvedCount} unresolved{resolvedCount > 0 ? `, ${resolvedCount} resolved` : ''})
          </h3>
          {resolvedCount > 0 && (
            <div className="flex flex-row items-center gap-2">
              <Checkbox
                id="show-resolved"
                checked={showResolved}
                onCheckedChange={(checked) => setShowResolved(checked === true)}
              />
              <Label
                htmlFor="show-resolved"
                className="text-xs text-zinc-600 cursor-pointer"
              >
                Show resolved
              </Label>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : (
          <>
            {filteredComments.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4 text-center">
                {showResolved ? 'No comments yet' : 'No unresolved comments'}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {filteredComments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    testItemId={testItemId}
                  />
                ))}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="resize-none"
                disabled={isAdding}
              />
              <div className="flex flex-row justify-end">
                <Button
                  type="submit"
                  size="sm"
                  disabled={!content.trim() || isAdding}
                >
                  {isAdding ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Comment'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(CommentsSection);
